import React from 'react';
import { Navigate } from 'react-router-dom';

import HelmetWrapper from 'components/atoms/HelmetWrapper';
import BackOfficeDrawerPusher from 'components/features/backOffice/BackOfficeDrawerPusher';
import BackOfficeNavigation from 'components/features/backOffice/BackOfficeNavigation';
import { ErrorBoundary, SentryRoute as Route, SentryRoutes as Routes } from 'config/sentry';
import CiboSubscriptionListPageContainer from 'containers/_pages/CiboSubscriptionListPageContainer';
import BackOfficeDeviceMap from 'routes/BackOfficeDeviceMap';
import BackOfficeDevices from 'routes/BackOfficeDevices';
import BackOfficeEnterprises from 'routes/BackOfficeEnterprises';
import BackOfficeInvitations from 'routes/BackOfficeInvitations';
import BackOfficeOperations from 'routes/BackOfficeOperations';
import BackOfficeOperationsDataTransfer from 'routes/BackOfficeOperationsDataTransfer';
import BackOfficeUsers from 'routes/BackOfficeUsers';
import { useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { ProtectedResource, ResourceAction } from 'types/action.enum';

function BackOffice(): JSX.Element {
  const canViewEnterpriseUsers = useMyUserHasInternalPermission({
    resource: ProtectedResource.ENTERPRISE_USER,
    action: ResourceAction.READ
  });
  const canViewInternalUsers = useMyUserHasInternalPermission({
    resource: ProtectedResource.INTERNAL_USER,
    action: ResourceAction.READ
  });
  const canAccessBackOfficeUsers = canViewEnterpriseUsers || canViewInternalUsers;
  const canAccessBackOfficeInvitations = useMyUserHasInternalPermission({
    resource: ProtectedResource.INVITATION,
    action: ResourceAction.READ
  });

  return (
    <ErrorBoundary>
      <BackOfficeDrawerPusher>
        <>
          <ErrorBoundary>
            <HelmetWrapper section="Back Office" />
            <BackOfficeNavigation />
          </ErrorBoundary>
          <ErrorBoundary>
            <Routes>
              <Route
                path="enterprises/:enterpriseId/cibo-subscriptions"
                element={<CiboSubscriptionListPageContainer />}
              />
              <Route
                path="enterprises"
                element={<BackOfficeEnterprises />}
              />
              <Route
                path="devices"
                element={<BackOfficeDevices />}
              />
              <Route
                path="device-map"
                element={<BackOfficeDeviceMap />}
              />
              <Route
                path="operations/data-transfer"
                element={<BackOfficeOperationsDataTransfer />}
              />
              <Route
                path="operations"
                element={<BackOfficeOperations />}
              />
              {canAccessBackOfficeUsers && (<Route
                path="users"
                element={<BackOfficeUsers />}
              />)}
              {canAccessBackOfficeInvitations && (<Route
                path="invitations"
                element={<BackOfficeInvitations />}
              />)}
              <Route
                path="/"
                element={<Navigate replace to="enterprises" />}
              />
            </Routes>
          </ErrorBoundary>
        </>
      </BackOfficeDrawerPusher>
    </ErrorBoundary>
  );
}

export default BackOffice;
